/* @flow */
import * as React from 'react';
import { observable, autorun } from 'mobx';
import { observer, inject } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import Button from '@material-ui/core/Button';
import RequireForAccess from 'react-access';
import Chip from '@material-ui/core/Chip';
import Avatar from '@material-ui/core/Avatar';
import TextField from '@material-ui/core/TextField';
import toCase from 'to-case';
import fastDeepEqual from 'fast-deep-equal';

import UserStore from 'stores/UserStore';
import { ElectroTooltip, SearchTextField } from 'components/ExtendedUiElements/customized';
import ErrorBox from 'components/ErrorBox';
import Loading from 'components/Loading/Loading';
import type { ErrorBoxProps } from 'components/ErrorBox';
import { stringToTimeRange } from 'stores/ReportViewStore';
import { downloadCsv } from 'permissions/general';
import { ComponentWithInjectedProps } from 'utils/injectedProps';

import type {
  ConsolidatedFilterItem,
  LayoutType,
  ViewConfig,
  UserSearchPreference,
} from 'tables/components/ReportView/types';
import styles from './PageSearch.module.scss';
import ActionDrawerStore from 'stores/ActionDrawerStore';
import LoadingWrapper from '../LoadingWrapper/LoadingWrapper';

export type PageSearchProps = {
  viewConfig: ViewConfig,
  filters?: ConsolidatedFilterItem[],
  layout?: LayoutType,
  count: number,
  searchTerm?: string,
  userSearchPreference?: UserSearchPreference,
  userSearchPreferenceLoading?: boolean,
  filterDrawerOpen?: boolean,
  filtersError?: ErrorBoxProps,
  onLayoutChangeClick?: (newLayout: LayoutType) => void,
  onFilterButtonClick?: () => void,
  onDownloadClick?: () => void,
  onSaveSearchClick?: (searchName: string, isUpdate?: boolean) => void,
  onSearchSubmit?: (searchTerm: string) => void,
  onClearSearch?: () => void,
  onDeleteFilterItem?: (filterItem: SingularConsolidatedFilterItem) => void,
};

export type SingularConsolidatedFilterItem = ConsolidatedFilterItem & {
  values: void,
  value: string,
};

const DOWNLOAD_LIMIT = 10000;

type InjectedProps = {
  userStore: UserStore,
  actionDrawerStore: ActionDrawerStore,
} & TranslatorProps;

@translate()
@inject('userStore', 'actionDrawerStore')
@observer
export default class PageSearch extends ComponentWithInjectedProps<
  PageSearchProps,
  {},
  InjectedProps
> {
  @observable searchTerm = '';

  @observable saveSearchActive = false;
  @observable saveSearchName: string;

  get userPreferencesWillUpdate(): boolean {
    if (this.props.userSearchPreference) {
      const {
        searchConfig: { filters, search },
      } = this.props.userSearchPreference;

      if (this.searchTerm !== search.join(' ')) {
        return true;
      }

      if (this.props.filters || filters) {
        if (!this.props.filters && filters.length) {
          return true;
        }

        const preferenceFilters = filters
          .map(filter => ({
            key: filter.key,
            values: [filter.value],
          }))
          .reduce((_preferenceFilters, filter) => {
            const targetFilter = _preferenceFilters.find(_filter => _filter.key === filter.key);

            if (targetFilter) {
              targetFilter.values.push(...filter.values);
            } else {
              _preferenceFilters.push({ ...filter });
            }

            return _preferenceFilters;
          }, [])
          .map(filter => ({
            ...filter,
            values: filter.values.sort(),
          }))
          .sort();
        const consolidatedFilters = (this.props.filters || [])
          .map(filter => ({
            key: filter.key,
            values: filter.values.sort(),
          }))
          .sort();

        return !fastDeepEqual(preferenceFilters, consolidatedFilters);
      }
    }

    return false;
  }

  _disposers: Function[] = [];

  componentDidMount() {
    this._disposers.push(
      autorun(() => {
        this.searchTerm = this.props.searchTerm || '';
      }),
      autorun(() => {
        if (this.saveSearchActive) {
          this.saveSearchName =
            (this.props.userSearchPreference && this.props.userSearchPreference.name) || '';
        }
      })
    );
  }

  componentWillUnmount() {
    for (const disposer of this._disposers) {
      disposer();
    }
  }

  // TODO: In the future, we need to have translations for the filter item names
  chipContentResolvers: { [string]: (filter: SingularConsolidatedFilterItem) => string } = {
    date: filter => {
      const { to, from } = stringToTimeRange(filter.value);

      if (!to) {
        return `${String(filter.name)}: from ${from}`;
      }

      if (!from) {
        return `${String(filter.name)}: up to ${to}`;
      }

      return `${String(filter.name)}: ${from} - ${to}`;
    },
    notfoundstring: filter => {
      const { labels } = filter;
      const value = filter.labels[filter.value] ? filter.value : Object.keys(labels)[0];
      return `${filter.name ? String(filter.name) + ': ' : ''}${toCase.title(String(value))}`;
    },
    string: filter => {
      const filterLabel = toCase.title(
        String((filter.labels && filter.labels[filter.value]) || filter.value)
      );
      var filterLabelTranslated = filterLabel;
      if (filter.key === 'er_status' || filter.key === 'er_order_type') {
        filterLabelTranslated = this.props.t('general:status.' + filterLabel.toLowerCase());
      }
      return `${filter.name ? String(filter.name) + ': ' : ''}${filterLabelTranslated}`;
    },
    boolean: filter =>
      `${filter.name ? String(filter.name) + ': ' : ''}${
        filter.value ? this.props.t('general:enabled') : this.props.t('general:disabled')
      }`,
  };

  handleSearchTextFieldChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
    this.searchTerm = e.target.value;
  };

  handleDeleteFilterItem = (item: SingularConsolidatedFilterItem) => {
    const { onDeleteFilterItem, filters: activeFilters } = this.props;

    if (onDeleteFilterItem) {
      onDeleteFilterItem(item, activeFilters);
    }
  };

  handleRemoveSearchTerm = () => {
    const { onClearSearch } = this.props;

    if (onClearSearch) {
      onClearSearch();
    }
  };

  makeDownloadClickHandler = key => {
    return e => {
      if (e && e.preventDefault) {
        e.preventDefault();
      }

      if (e && e.stopPropagation) {
        e.stopPropagation();
      }

      if (!this.props.count || this.props.count <= DOWNLOAD_LIMIT) {
        this.props.onDownloadClick(key);
      }
    };
  };

  renderFilterButton = () => {
    const {
      viewConfig: { disableFilters },
      onFilterButtonClick,
      filterDrawerOpen,
      t,
    } = this.props;

    if (!disableFilters) {
      return (
        <ElectroTooltip title={t('general:labels.show_filters')}>
          <Button
            type="button"
            className={`${styles.button} ${filterDrawerOpen ? styles.active : ''}`}
            variant="contained"
            color="secondary"
            onClick={onFilterButtonClick}
          >
            <svg className={styles.icon}>
              <use xlinkHref="#icon-filter" />
            </svg>
          </Button>
        </ElectroTooltip>
      );
    }
  };

  renderDataViewControls = () => {
    const {
      viewConfig: { supportedLayouts, disableDownload, downloadPermissions },
      layout,
      onLayoutChangeClick,
      onDownloadClick,
      t,
      count,
    } = this.props;

    const downloadTooltip =
      count >= DOWNLOAD_LIMIT ? t('general:actions.download_limit') : t('general:actions.download');

    const supports = {
      grid: !!supportedLayouts && supportedLayouts.includes('GRID'),
      table: !!supportedLayouts && supportedLayouts.includes('TABLE'),
    };

    const supportsMoreThanOneLayout = Object.values(supports).filter(value => !!value).length > 1;

    return (
      <div className={styles['button-group']}>
        {supportsMoreThanOneLayout && supports.grid && (
          <ElectroTooltip title={t('general:labels.grid_view')}>
            <Button
              type="button"
              className={`${styles.button} ${layout === 'GRID' ? styles.active : ''}`}
              variant="contained"
              color="secondary"
              onClick={() => onLayoutChangeClick && onLayoutChangeClick('GRID')}
            >
              <svg className={styles.icon}>
                <use xlinkHref="#icon-grid-layout" />
              </svg>
            </Button>
          </ElectroTooltip>
        )}
        {supportsMoreThanOneLayout && supports.table && (
          <ElectroTooltip title={t('general:labels.table_view')}>
            <Button
              type="button"
              className={`${styles.button} ${layout === 'TABLE' ? styles.active : ''}`}
              variant="contained"
              color="secondary"
              onClick={() => onLayoutChangeClick && onLayoutChangeClick('TABLE')}
            >
              <svg className={styles.icon}>
                <use xlinkHref="#icon-table-layout" />
              </svg>
            </Button>
          </ElectroTooltip>
        )}
        {!disableDownload && onDownloadClick && (
          <RequireForAccess permissions={downloadPermissions || downloadCsv}>
            <LoadingWrapper auto={false} type={`download-csv-${this.props.viewConfig.codename}`}>
              {(key, loading) => (
                <ElectroTooltip
                  title={downloadTooltip}
                  classes={{
                    arrowWrapper:
                      count >= DOWNLOAD_LIMIT ? 'page-search-download-tooltip-wrapper' : '',
                    tooltipText: count >= DOWNLOAD_LIMIT ? 'page-search-download-tooltip-text' : '',
                  }}
                >
                  <Button
                    type="button"
                    className={`${styles.button} ${styles.cloud}`}
                    variant="contained"
                    color="secondary"
                    disabled={loading}
                    onClick={this.makeDownloadClickHandler(key)}
                  >
                    <svg className={styles.icon}>
                      <use xlinkHref="#icon-download" />
                    </svg>
                  </Button>
                </ElectroTooltip>
              )}
            </LoadingWrapper>
          </RequireForAccess>
        )}
      </div>
    );
  };

  renderChip = (item: ConsolidatedFilterItem | string, onDelete: () => void) => {
    const isSearchTerm = typeof item === 'string';
    const chipLabel = isSearchTerm ? item : this.chipContentResolvers[item.type || 'string'](item);
    const isFixed = !isSearchTerm && item.fixed;

    const chipDeleteProps =
      (!isFixed && {
        deleteIcon: (
          <svg>
            <use xlinkHref="#icon-close" />
          </svg>
        ),
        onDelete,
      }) ||
      {};

    return (
      <li
        key={isSearchTerm ? `search-term-chip-${item}` : item.value}
        className={`${styles.chip} ${item.doesNotMatchAvailableFilters ? styles.doesNotMatch : ''}`}
      >
        <Chip
          avatar={
            <Avatar>
              <svg>
                <use xlinkHref={isSearchTerm ? '#icon-search' : '#icon-filter'} />
              </svg>
            </Avatar>
          }
          label={chipLabel}
          {...chipDeleteProps}
        />
      </li>
    );
  };

  renderFiltersErrorSection = () => {
    const { filtersError } = this.props;

    if (filtersError) {
      return (
        <div className={styles.errorSection}>
          <ErrorBox {...filtersError} />
        </div>
      );
    }
  };

  renderFilterChipsSection = () => {
    const {
      filters = [],
      searchTerm,
      userStore,
      onSaveSearchClick,
      userSearchPreferenceLoading,
      viewConfig: { disableUserPreferenceSave, disableFilterChips },
      t,
    } = this.props;
    if (filters.length === 0 && !searchTerm) {
      return null;
    }

    const fixedFilters: SingularConsolidatedFilterItem[] = [];
    const removableFilters: SingularConsolidatedFilterItem[] = [];

    if (!disableFilterChips) {
      for (const filter of filters) {
        // Skip CaaS Asset filter since it is used as a
        // Toggle instead of  a regular filter.
        if (filter.key === 'is_caas_asset') {
          continue;
        }

        if (filter.fixed) {
          fixedFilters.push(...expandFilterItem(filter));
        } else {
          removableFilters.push(...expandFilterItem(filter));
        }
      }
    }

    if (!searchTerm && fixedFilters.length === 0 && removableFilters.length === 0) {
      return null;
    }

    return (
      <div className={styles['page-search-filters']}>
        <ul>
          {fixedFilters.map(item => this.renderChip(item, () => this.handleDeleteFilterItem(item)))}
        </ul>
        <ul>
          {removableFilters.map(item =>
            this.renderChip(item, () => this.handleDeleteFilterItem(item))
          )}
          {!!searchTerm && this.renderChip(searchTerm, this.handleRemoveSearchTerm)}
        </ul>
        {!disableUserPreferenceSave && (
          <div className={styles['button-wrapper']}>
            {userSearchPreferenceLoading && (
              <div className={styles.loadingWrapper}>
                <Loading variant="small" />
              </div>
            )}

            {!this.saveSearchActive ? (
              <div>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    (userStore.activeUser && userStore.isReadOnly) || userSearchPreferenceLoading
                  }
                  onClick={() => {
                    this.saveSearchActive = true;
                  }}
                >
                  {this.userPreferencesWillUpdate
                    ? t('general:actions.update')
                    : t('general:actions.save')}
                </Button>
              </div>
            ) : null}
            {this.saveSearchActive ? (
              <div className={styles['save-box']}>
                <TextField
                  margin="normal"
                  placeholder={t('general:placeholders.save_filters')}
                  onChange={(e: SyntheticInputEvent<HTMLInputElement>) => {
                    this.saveSearchName = e.target.value;
                  }}
                  value={this.saveSearchName}
                  autoComplete="on"
                  className={styles['search-bar']}
                />
                <Button
                  onClick={() => {
                    onSaveSearchClick &&
                      onSaveSearchClick(this.saveSearchName, this.userPreferencesWillUpdate);

                    this.saveSearchActive = false;
                  }}
                  variant="contained"
                  color="primary"
                  disabled={!this.saveSearchName}
                >
                  {this.userPreferencesWillUpdate
                    ? t('general:actions.update')
                    : t('general:actions.save')}
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    this.saveSearchActive = false;
                  }}
                >
                  {t('general:actions.cancel')}
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  render() {
    const { onSearchSubmit, t } = this.props;

    return (
      <div className={styles['page-search-form']}>
        <div className={styles['page-search-wrapper']}>
          <form
            onSubmit={e => {
              e.preventDefault();
              if (typeof this.searchTerm === 'string' && onSearchSubmit) {
                onSearchSubmit(this.searchTerm);
              }
            }}
          >
            {this.renderFilterButton()}
            <SearchTextField
              placeholder={t('general:placeholders.search')}
              onChange={this.handleSearchTextFieldChange}
              value={this.searchTerm}
            />
            {this.renderDataViewControls()}
          </form>
        </div>

        {this.renderFiltersErrorSection()}
        {this.renderFilterChipsSection()}
      </div>
    );
  }
}

const expandFilterItem = (item: ConsolidatedFilterItem): SingularConsolidatedFilterItem[] =>
  item.values.map(itemValue => {
    const mappedItem = {
      ...item,
      value: itemValue,
    };
    delete mappedItem.values;

    return mappedItem;
  });
